import { motion } from "framer-motion";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";
import { WaterCalculators, WattCalculator } from "./Calculators";
import { TsunamiZone } from "./TsunamiZone";

export function GuideTab({ guide, lowBandwidth }) {
  const { lang } = useLang();

  const Reveal = ({ children, delay = 0 }) =>
    lowBandwidth ? (
      <div>{children}</div>
    ) : (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    );

  return (
    <div data-testid={`guide-${guide.id}`}>
      <div className="relative overflow-hidden border-b border-slate-300 bg-[#1C2B4A] text-white">
        {guide.image && (
          <img
            src={guide.image}
            alt=""
            loading="lazy"
            className="deco absolute inset-0 h-full w-full object-cover opacity-25"
          />
        )}
        <div className="relative px-4 sm:px-8 lg:px-12 py-12 sm:py-16 max-w-5xl">
          <Reveal>
            <p className="mb-4 flex items-center gap-2.5 font-mono-pr text-xs font-bold uppercase tracking-[0.2em] text-amber-500">
              <Icon name={guide.icon} className="w-5 h-5" />
              {tx(ui.tabs[guide.id], lang)}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{tx(guide.title, lang)}</h2>
            <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">{tx(guide.subtitle, lang)}</p>
          </Reveal>
        </div>
      </div>

      <div className="px-4 sm:px-8 lg:px-12 py-10 sm:py-14 max-w-5xl">
        {guide.chapters.map((ch, i) => (
          <Reveal key={i}>
            <section data-testid={`guide-${guide.id}-chapter-${i + 1}`} className="mb-12 sm:mb-16 last:mb-0">
              <div className="flex items-baseline gap-4 sm:gap-6 mb-4">
                <span className="font-display text-5xl sm:text-7xl font-black tracking-tighter text-outline select-none" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className="font-mono-pr text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700">
                    {tx(ui.guide.chapter, lang)} {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">
                    {tx(ch.title, lang)}
                  </h3>
                </div>
              </div>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5 max-w-3xl">{tx(ch.intro, lang)}</p>
              <div className="rounded-lg border border-slate-300 bg-white p-6 sm:p-8">
                <p className="mb-4 font-mono-pr text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  {tx(ui.guide.steps, lang)}
                </p>
                <ol className="space-y-3.5">
                  {ch.steps.map((s, j) => (
                    <li key={j} className="flex gap-3.5 text-base leading-relaxed text-slate-800">
                      <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1C2B4A] font-mono-pr text-[11px] font-bold text-white">
                        {j + 1}
                      </span>
                      {tx(s, lang)}
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          </Reveal>
        ))}

        {guide.id === "agua" && <WaterCalculators />}
        {guide.id === "energia" && <WattCalculator />}
        {guide.id === "evacuacion" && <TsunamiZone />}
      </div>
    </div>
  );
}
